import {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useLocation } from "react-router-dom";

import NavLg from "./NavLg";
import Search from "./Search";

const PRODUCTS_API_URL = "https://admin.monsinidress.com/api/products/brand";
const SEARCH_REQUEST_TIMEOUT = 25000;

let productsCache = null;

function Navbar() {
	const { pathname } = useLocation();
	const [isScrollingUp, setIsScrollingUp] = useState(true);
	const [menu, setMenu] = useState(false);
	const [searchbtn, setSearchBtn] = useState(false);
	const [search, setSearch] = useState("");
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	const searchRef = useRef(null);
	const lastScrollYRef = useRef(0);
	const shouldLoadSearchProducts = searchbtn && search.trim().length >= 2;

	// Only fetch the large search catalogue after the visitor actually types
	// at least two characters. Opening the search overlay alone does no network work.
	useEffect(() => {
		if (!shouldLoadSearchProducts) {
			setLoading(false);
			return undefined;
		}

		if (Array.isArray(productsCache)) {
			setData(productsCache);
			setError("");
			setLoading(false);
			return undefined;
		}

		let active = true;
		let didTimeout = false;
		const controller = new AbortController();
		const timeoutId = window.setTimeout(() => {
			didTimeout = true;
			controller.abort();
		}, SEARCH_REQUEST_TIMEOUT);

		async function fetchProducts() {
			setLoading(true);
			setError("");

			try {
				const response = await fetch(PRODUCTS_API_URL, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ brand: "Monsini" }),
					signal: controller.signal,
				});

				if (!response.ok) {
					throw new Error(`Products request failed: ${response.status}`);
				}

				const result = await response.json();
				const products = result?.data?.products;

				if (!Array.isArray(products)) {
					throw new Error("The products API returned an invalid response.");
				}

				if (!active) return;
				productsCache = products;
				setData(productsCache);
			} catch (requestError) {
				if (!active) return;
				if (requestError?.name === "AbortError") {
					if (didTimeout) {
						setError("Product search is taking longer than expected. Please try again.");
						setData([]);
					}
					return;
				}
				setData([]);
				setError("Products could not be loaded. Please try again later.");
			} finally {
				window.clearTimeout(timeoutId);
				if (active) setLoading(false);
			}
		}

		fetchProducts();
		return () => {
			active = false;
			window.clearTimeout(timeoutId);
			controller.abort();
		};
	}, [shouldLoadSearchProducts]);

	const searchResult = useMemo(() => {
		const value = search.trim().toLowerCase();
		if (value.length < 2) return { count: 0, products: [] };

		const matches = data.filter((product) => {
			const style = String(product?.style || "").toLowerCase();
			const category = String(product?.category || "").toLowerCase();
			const brand = String(product?.brand || "").toLowerCase();
			return style.includes(value) || category.includes(value) || brand.includes(value);
		});

		return {
			count: matches.length,
			products: matches.slice(0, 24),
		};
	}, [data, search]);

	const handleSearchOpen = useCallback(() => {
		setMenu(false);
		setSearchBtn(true);
	}, []);

	useEffect(() => {
		let ticking = false;
		const handleScroll = () => {
			if (ticking) return;
			ticking = true;
			window.requestAnimationFrame(() => {
				const currentScrollY = window.scrollY;
				if (currentScrollY < 80) {
					setIsScrollingUp(true);
				} else {
					setIsScrollingUp(currentScrollY < lastScrollYRef.current);
				}
				lastScrollYRef.current = currentScrollY;
				ticking = false;
			});
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	useEffect(() => {
		setSearch("");
		setSearchBtn(false);
		setMenu(false);
	}, [pathname]);

	useEffect(() => {
		if (!searchbtn && !menu) return undefined;
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [menu, searchbtn]);

	useEffect(() => {
		if (!searchbtn) return undefined;
		const handleEscape = (event) => {
			if (event.key === "Escape") {
				setSearch("");
				setSearchBtn(false);
			}
		};
		document.addEventListener("keydown", handleEscape);
		return () => document.removeEventListener("keydown", handleEscape);
	}, [searchbtn]);

	return (
		<div className="relative">
			<Search
				filteredData={searchResult.products}
				search={search}
				setSearch={setSearch}
				len={searchResult.count}
				searchbtn={searchbtn}
				searchRef={searchRef}
				setSearchBtn={setSearchBtn}
				loading={loading}
				error={error}
			/>

			<NavLg
				menu={menu}
				setMenu={setMenu}
				onOpenSearch={handleSearchOpen}
				isScrollingUp={isScrollingUp}
			/>
		</div>
	);
}

export default memo(Navbar);
